/**
 * App shell. Renders:
 *   - <Auth/> when not signed in (split-screen brand panel),
 *   - otherwise a sidebar (wide) or bottom-nav (narrow) layout with a routed
 *     view inside the main pane.
 *
 * State that survives reloads: theme, route, route payload (eg. selected
 * session id). Authentication tokens and prefs live in stores in `./store`.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import * as api from "./api";
import { authStore } from "./store";
import { Icon } from "./icons";
import { Wordmark } from "./logo";
import { IconBtn, getInitialTheme, persistTheme, useMedia } from "./ui";
import { AuthScreen } from "./screens/AuthScreen";
import { Dashboard } from "./screens/Dashboard";
import { LiveScreen } from "./screens/LiveScreen";
import { History } from "./screens/History";
import { SessionScreen } from "./screens/Session";
import { EnrollScreen } from "./screens/EnrollScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { DemoScreen } from "./screens/DemoScreen";
import { AdminScreen } from "./screens/AdminScreen";
import { LearnScreen } from "./screens/LearnScreen";
import { ErrorBoundary } from "./ErrorBoundary";

export type Route = "home" | "live" | "history" | "session" | "enroll" | "learn" | "settings" | "admin";
export type Go = (r: Route, payload?: { sessionId?: number }) => void;

const LS_ROUTE = "tw.route";
const LS_PAYLOAD = "tw.payload";

const NAV: { route: Route; icon: string; label: string }[] = [
  { route: "home", icon: "messages", label: "Home" },
  { route: "live", icon: "waves", label: "Live" },
  { route: "learn", icon: "sparkle", label: "Learn" },
  { route: "history", icon: "history", label: "History" },
  { route: "enroll", icon: "mic", label: "Voice" },
  { route: "settings", icon: "settings", label: "Settings" },
];

/** Nav items shown for the current user — appends the admin entry only when
 *  the server flagged this account as an admin. */
function navFor(isAdmin: boolean): { route: Route; icon: string; label: string }[] {
  return isAdmin ? [...NAV, { route: "admin", icon: "shield", label: "Admin" }] : NAV;
}

export function App() {
  const tokens = authStore((s) => s.tokens);
  const user = authStore((s) => s.user);
  const isDemo = authStore((s) => s.isDemo);
  const [theme, setTheme] = useState<"light" | "dark">(() => getInitialTheme());
  const [route, setRoute] = useState<Route>(() => (localStorage.getItem(LS_ROUTE) as Route | null) ?? "home");
  const [payload, setPayload] = useState<{ sessionId?: number }>(() => {
    try { return JSON.parse(localStorage.getItem(LS_PAYLOAD) ?? "{}"); } catch { return {}; }
  });
  const [enrollments, setEnrollments] = useState<api.EnrollmentSummary[]>([]);
  const [loadingMe, setLoadingMe] = useState(false);
  const narrow = useMedia("(max-width: 860px)");
  const tiny = useMedia("(max-width: 880px)");

  const go: Go = (r, p) => {
    setRoute(r);
    const np = p ?? {};
    setPayload(np);
    try {
      localStorage.setItem(LS_ROUTE, r);
      localStorage.setItem(LS_PAYLOAD, JSON.stringify(np));
    } catch { /* ignore */ }
  };

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    persistTheme(next);
  };

  useEffect(() => {
    if (!tokens) return;
    if (isDemo) {
      // Demo flow handles /me + enrollments inline; skip the global load to
      // avoid persisting user data and to keep state minimal.
      if (!authStore.getState().user) {
        api.getMe().then((u) => authStore.getState().setUser(u)).catch(() => {});
      }
      return;
    }
    setLoadingMe(true);
    const tasks: Promise<unknown>[] = [];
    if (!authStore.getState().user) tasks.push(api.getMe().then((u) => authStore.getState().setUser(u)));
    tasks.push(api.listEnrollments().then(setEnrollments).catch(() => setEnrollments([])));
    Promise.allSettled(tasks).finally(() => setLoadingMe(false));
  }, [tokens, isDemo]);

  if (!tokens) {
    return (
      <div className="hc-root" data-theme={theme} style={{ minHeight: "100vh", background: "var(--bg)" }}>
        <AuthScreen
          theme={theme} toggleTheme={toggleTheme} tiny={tiny}
          onAuthed={(hasActive) => {
            api.listEnrollments().then(setEnrollments).catch(() => {});
            go(hasActive ? "home" : "enroll");
          }}
        />
      </div>
    );
  }

  if (isDemo) {
    // Demo runs in its own shell — no sidebar, no bottom nav, no history.
    return (
      <div className="hc-root" data-theme={theme} style={{ minHeight: "100vh", background: "var(--bg-sunken)", color: "var(--ink)" }}>
        <DemoScreen theme={theme} toggleTheme={toggleTheme} />
      </div>
    );
  }

  if (loadingMe && !user) {
    return (
      <div className="hc-root" data-theme={theme} style={{ display: "grid", placeItems: "center", minHeight: "100vh", color: "var(--ink-3)" }}>
        Loading…
      </div>
    );
  }

  const hasActiveEnrollment = enrollments.some((e) => e.active);
  const liveRoute = route === "live";

  return (
    <div className="hc-root" data-theme={theme}
      style={{ display: "flex", minHeight: "100vh", background: "var(--bg-sunken)", color: "var(--ink)" }}>
      {!narrow && <Sidebar route={route} go={go} user={user} theme={theme} toggleTheme={toggleTheme} hasActive={hasActiveEnrollment} />}
      <main className="hc-scroll" style={{
        flex: 1, minWidth: 0,
        // Live route needs a definite height so LiveScreen's `height: 100%`
        // resolves; other routes still scroll the document as before.
        height: liveRoute ? "100dvh" : undefined,
        minHeight: liveRoute ? 0 : "100vh",
        overflowY: liveRoute ? "hidden" : "auto",
        paddingBottom: narrow ? 76 : 0,
      }}>
        <ErrorBoundary key={route}>
          <RouteView route={route} go={go} payload={payload}
            enrollments={enrollments} setEnrollments={setEnrollments}
            theme={theme} toggleTheme={toggleTheme} isAdmin={!!user?.is_admin} />
        </ErrorBoundary>
      </main>
      {narrow && <BottomNav route={route} go={go} hasActive={hasActiveEnrollment} isAdmin={!!user?.is_admin} />}
    </div>
  );
}

function RouteView({
  route, go, payload, enrollments, setEnrollments, theme, toggleTheme, isAdmin,
}: {
  route: Route; go: Go; payload: { sessionId?: number };
  enrollments: api.EnrollmentSummary[];
  setEnrollments: (e: api.EnrollmentSummary[]) => void;
  theme: "light" | "dark"; toggleTheme: () => void;
  isAdmin: boolean;
}) {
  switch (route) {
    case "home":
      return <Dashboard go={go} enrollments={enrollments} />;
    case "live":
      return <LiveScreen go={go} hasActive={enrollments.some((e) => e.active)} />;
    case "history":
      return <History go={go} />;
    case "session":
      return payload.sessionId
        ? <SessionScreen sessionId={payload.sessionId} go={go} />
        : <History go={go} />;
    case "learn":
      return <LearnScreen />;
    case "enroll":
      return <EnrollScreen
        enrollments={enrollments}
        onChange={() => api.listEnrollments().then(setEnrollments).catch(() => {})}
        go={go}
      />;
    case "settings":
      return <SettingsScreen theme={theme} toggleTheme={toggleTheme} go={go} />;
    case "admin":
      // Defense-in-depth: the nav entry is already admin-only, but never
      // render the admin surface (not even its token prompt) for a
      // non-admin account that somehow lands on this route. The server is
      // still the real gate; this just keeps the UI honest.
      return isAdmin ? <AdminScreen /> : <Dashboard go={go} enrollments={enrollments} />;
    default:
      return null;
  }
}

function Sidebar({
  route, go, user, theme, toggleTheme, hasActive,
}: {
  route: Route; go: Go; user: api.UserOut | null;
  theme: "light" | "dark"; toggleTheme: () => void; hasActive: boolean;
}) {
  return (
    <aside style={{
      width: 256, flexShrink: 0, padding: "22px 18px 18px",
      background: "var(--surface)", borderRight: "1px solid var(--border)",
      display: "flex", flexDirection: "column", gap: 6, position: "sticky", top: 0, height: "100vh",
    }}>
      <div style={{ padding: "0 6px 18px" }}>
        <Wordmark size={26} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {navFor(!!user?.is_admin).map((n) => (
          <NavItem key={n.route} item={n} active={route === n.route} dot={n.route === "enroll" && !hasActive} onClick={() => go(n.route)} />
        ))}
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 8px" }}>
        <button onClick={() => go("settings")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          <Avatar name={user?.display_name ?? "?"} />
        </button>
        <IconBtn name={theme === "dark" ? "sun" : "moon"} onClick={toggleTheme} variant="surface" size={38} ic={18} />
      </div>
    </aside>
  );
}

function NavItem({
  item, active, onClick, dot,
}: { item: { route: Route; icon: string; label: string }; active: boolean; onClick: () => void; dot?: boolean }) {
  const style: CSSProperties = {
    display: "flex", alignItems: "center", gap: 13, padding: "11px 14px", borderRadius: 14,
    cursor: "pointer", border: "none", textAlign: "left",
    background: active ? "var(--accent-soft)" : "transparent",
    color: active ? "var(--accent-ink)" : "var(--ink-2)",
    fontFamily: "var(--font-ui)", fontSize: 14.5, fontWeight: active ? 700 : 600,
    position: "relative",
  };
  return (
    <button onClick={onClick} style={style}>
      <Icon name={item.icon} size={20} stroke={active ? 2.3 : 2} />
      <span>{item.label}</span>
      {dot && <span style={{
        width: 7, height: 7, borderRadius: "50%", background: "var(--accent)", marginLeft: "auto",
      }} />}
    </button>
  );
}

function BottomNav({ route, go, hasActive, isAdmin }: { route: Route; go: Go; hasActive: boolean; isAdmin: boolean }) {
  return (
    <nav style={{
      position: "fixed", left: 0, right: 0, bottom: 0, height: 64, zIndex: 50,
      background: "var(--surface)", borderTop: "1px solid var(--border)",
      display: "flex", justifyContent: "space-around", alignItems: "center",
    }}>
      {navFor(isAdmin).map((n) => {
        const active = route === n.route;
        return (
          <button key={n.route} onClick={() => go(n.route)} style={{
            background: "transparent", border: "none", cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
            color: active ? "var(--accent)" : "var(--ink-3)",
            fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, position: "relative",
          }}>
            <Icon name={n.icon} size={22} stroke={active ? 2.3 : 2} />
            <span>{n.label}</span>
            {n.route === "enroll" && !hasActive && (
              <span style={{ position: "absolute", top: 4, right: "calc(50% - 16px)", width: 6, height: 6, borderRadius: "50%", background: "var(--accent)" }} />
            )}
          </button>
        );
      })}
    </nav>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = useMemo(
    () => name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]!.toUpperCase()).join("") || "?",
    [name],
  );
  return (
    <div style={{
      width: 38, height: 38, borderRadius: "50%", background: "var(--you-bubble)",
      color: "var(--you-ink)", display: "grid", placeItems: "center",
      fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14.5,
    }}>{initials}</div>
  );
}
